import { Control, Controller, FieldValues, useWatch } from 'react-hook-form';
import { Box, Grid2, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { IAttributeDef } from '@/src/config/AttributesConfig';
import {
  DYNAMIC_INPUT_TYPES_ENUM,
  ILooseObject,
  INPUT_FIELD_TYPE_MAP,
  INPUT_TYPES_ENUM,
  IAttributeOptions
} from '@/src/types/common.interface';
import DynamicInput from '@/src/components/common/DynamicInput';
import FormattedUILabel from '@/src/components/common/FormattedUILabel';

type MetaInputValuesProps = {
  handleOnDelete: () => void;
  disabled: boolean;
  availableAttributes: IAttributeDef[];
  allFields: ILooseObject[];
  currentIndex: number;
  control: Control<FieldValues>;
  errors?: ILooseObject;
  fieldName: string;
  onAttributeChange?: (fieldName: string, value: any) => void;
  clearErrors?: (name: string) => void;
};

const MetaInputValues = (props: MetaInputValuesProps) => {
  const {
    availableAttributes,
    handleOnDelete,
    disabled,
    allFields,
    currentIndex,
    errors,
    control,
    fieldName,
    onAttributeChange,
    clearErrors
  } = props;

  // Watch the selected attribute name to update value input options
  const selectedAttributeName = useWatch({
    control,
    name: `${fieldName}.selectedAttributeName`
  });

  // Get previously selected attribute names (excluding current field)
  const previouslySelectedAttributes = allFields
    .filter((field, index) => index !== currentIndex && field.selectedAttributeName)
    .map((field) => field.selectedAttributeName);

  // Filter out previously selected attributes and create options
  const filteredAttributes = availableAttributes.filter(
    (attr) => !previouslySelectedAttributes.includes(attr.name)
  );

  // Create options for attribute selection
  const attributeOptions: IAttributeOptions[] = filteredAttributes.map((attr) => ({
    value: attr.name,
    label: attr.ui_label
  }));

  return (
    <Box borderRadius={2} p={1} sx={{ backgroundColor: 'background.paper', boxShadow: 1 }}>
      <Box display="flex" justifyContent="flex-end">
        <IconButton aria-label="delete" size="small" onClick={handleOnDelete} disabled={disabled}>
          <DeleteIcon fontSize="inherit" color={disabled ? 'disabled' : 'error'} />
        </IconButton>
      </Box>
      <Grid2 container columnSpacing={1}>
        {/* Attribute Selection Controller */}
        <Controller
          control={control}
          name={`${fieldName}.selectedAttributeName`}
          render={({ field: { onChange, onBlur, value }, fieldState }) => {
            // const selectedAttribute = availableAttributes.find((attr) => attr.name === value);

            // Get error for this specific field
            const fieldError = fieldState.error?.message || errors?.selectedAttributeName?.message;

            return (
              <Grid2 size={6}>
                <FormattedUILabel uiLabel="Attribute" isRequired={true} />
                <DynamicInput
                  type={DYNAMIC_INPUT_TYPES_ENUM.SELECT}
                  id={currentIndex * 1000 + 1}
                  value={value || ''}
                  inputType={INPUT_TYPES_ENUM.TEXT}
                  hasError={!!fieldError}
                  helperText={fieldError ?? ' '}
                  onChange={(newValue: any) => {
                    onChange(newValue);
                    // Clear errors for both fields
                    if (clearErrors) {
                      clearErrors(`${fieldName}.selectedAttributeName`);
                      clearErrors(`${fieldName}.attributeValue`);
                    }
                    // Notify parent to clear the value when attribute changes
                    if (onAttributeChange) {
                      onAttributeChange(`${fieldName}.attributeValue`, '');
                    }
                  }}
                  onBlur={onBlur}
                  disabled={disabled || attributeOptions.length === 0}
                  options={attributeOptions}
                  placeholder={
                    attributeOptions.length === 0
                      ? 'No attributes available'
                      : 'Select an attribute'
                  }
                />
              </Grid2>
            );
          }}
        />

        {/* Value Input Controller */}
        <Controller
          control={control}
          name={`${fieldName}.attributeValue`}
          render={({ field: { onChange, onBlur, value }, fieldState }) => {
            // Find the selected attribute based on the watched value
            const selectedAttribute = availableAttributes.find(
              (attr) => attr.name === selectedAttributeName
            );

            // Get error for this specific field
            const fieldError = fieldState.error?.message || errors?.attributeValue?.message;

            // Get the selected attribute's options for the value input
            const valueOptions = selectedAttribute?.options ?? [];

            // Get input types for the selected attribute
            const valueInputType = selectedAttribute
              ? DYNAMIC_INPUT_TYPES_ENUM[
                  selectedAttribute.ui_type?.toUpperCase() as keyof typeof DYNAMIC_INPUT_TYPES_ENUM
                ]
              : DYNAMIC_INPUT_TYPES_ENUM.TEXT;

            const valueInputFieldType: INPUT_TYPES_ENUM = selectedAttribute
              ? INPUT_FIELD_TYPE_MAP[selectedAttribute.value_type?.toUpperCase()]
              : INPUT_TYPES_ENUM.TEXT;

            return (
              <Grid2 size={6}>
                <FormattedUILabel
                  uiLabel="Value"
                  isRequired={selectedAttribute?.is_mandatory || false}
                />
                <DynamicInput
                  type={valueInputType}
                  id={currentIndex * 1000 + 2}
                  value={
                    value ||
                    (valueInputType === DYNAMIC_INPUT_TYPES_ENUM.MULTI_SELECT ||
                    valueInputType === DYNAMIC_INPUT_TYPES_ENUM.DYNAMIC_MULTI_CHIPS_SELECT
                      ? []
                      : '')
                  }
                  inputType={valueInputFieldType}
                  hasError={!!fieldError}
                  helperText={fieldError ?? ' '}
                  onChange={(newValue: any) => {
                    onChange(newValue);
                    // Clear errors for the value field
                    if (clearErrors) {
                      clearErrors(`${fieldName}.attributeValue`);
                    }
                  }}
                  onBlur={onBlur}
                  disabled={disabled || !selectedAttribute}
                  options={valueOptions}
                  placeholder={selectedAttribute?.placeholder || 'Enter value'}
                  maxLength={selectedAttribute?.max_length}
                  disableClearable={selectedAttribute?.disable_clearable}
                />
              </Grid2>
            );
          }}
        />
      </Grid2>
    </Box>
  );
};

export default MetaInputValues;
